import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { PageBody, PageHeader } from '../../components/PageHeader'
import type { SiteVariety, VarietyProduct } from '../sites/structure'
import styles from './GodMode.module.css'

// Varieties (migration 202). Dataphyll's products (Cherries, Apples, ...) and
// the master list of varieties under each (Cherries / Lapins), kept here;
// each client has one list used by every one of its Sites, taken from the
// master list or its own, and every variety names its product. Nothing is
// deleted or renamed: rows, jobs and stock carry the names, so a wrong one is
// archived and the right one added.

export interface CatalogueVariety {
  id: string
  product_id: string
  name: string
  created_by: string | null
  created_at: string
  archived_at: string | null
  // How many supported clients have it on their list.
  clients: number
}
export interface CatalogueProduct {
  id: string
  name: string
  created_by: string | null
  created_at: string
  archived_at: string | null
  varieties: CatalogueVariety[]
}
export interface VarietiesResponse {
  products: CatalogueProduct[]
  clients: { id: string; name: string; varieties: number; needs_product: number }[]
}

const message = (e: any, fallback: string) => e?.body?.error || e?.message || fallback

// Runs one write at a time, then reads the page again; a refusal shows the server's reason.
function useWrites(reload: () => Promise<void>) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState('')
  const run = async (on: string, fn: () => Promise<string>) => {
    if (busy) return false
    setBusy(on)
    setError(null)
    setNotice('')
    try {
      const done = await fn()
      await reload()
      setNotice(done)
      return true
    } catch (e: any) {
      setError(message(e, 'Nothing was changed'))
      return false
    } finally {
      setBusy(null)
    }
  }
  return { busy, error, setError, notice, run }
}

export function VarietiesPage() {
  const { user } = useAuth()
  const [data, setData] = useState<VarietiesResponse | null>(null)
  const [productName, setProductName] = useState('')
  const [productId, setProductId] = useState('')
  const [name, setName] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const load = async () => setData(await apiJson<VarietiesResponse>(user, '/api/god-mode/varieties'))
  const w = useWrites(load)

  useEffect(() => {
    load().catch((e) => w.setError(message(e, 'Could not load the varieties')))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const live = (data?.products || []).filter((p) => !p.archived_at)
  const shown = (data?.products || []).filter((p) => showArchived || !p.archived_at)
  const archivedCount = (data?.products || []).reduce((n, p) => n + (p.archived_at ? 1 : 0) + p.varieties.filter((v) => v.archived_at).length, 0)

  const addProduct = async () => {
    const n = productName.trim()
    if (!n) return
    if (await w.run('product', async () => {
      await apiJson(user, '/api/god-mode/products', { method: 'POST', body: { name: n } })
      return `${n} added as a product.`
    })) setProductName('')
  }
  const addVariety = async () => {
    const n = name.trim()
    const p = live.find((x) => x.id === productId)
    if (!n || !p) return
    if (await w.run('variety', async () => {
      await apiJson(user, '/api/god-mode/master-varieties', { method: 'POST', body: { product_id: p.id, name: n } })
      return `${p.name} / ${n} added to the master list.`
    })) setName('')
  }
  const toggle = (path: 'products' | 'master-varieties', id: string, label: string, archived: boolean) =>
    w.run(id, async () => {
      await apiJson(user, `/api/god-mode/${path}/${id}/${archived ? 'restore' : 'archive'}`, { method: 'POST', body: {} })
      return archived ? `${label} restored.` : `${label} archived: offered to no client. Clients that have it keep it.`
    })

  return (
    <>
      <PageHeader title="Varieties" />
      <PageBody>
        <div className={styles.stack}>
          <p className={styles.lead}>
            Products and the master list of varieties under each. Each client has one variety list for all its Sites: taken from here, or its own, always under a product. A name is never
            edited; archive a wrong one and add the right one.
          </p>
          {w.error && <p className={styles.error} role="alert">{w.error}</p>}
          {w.notice && <p className={styles.saved} role="status">{w.notice}</p>}
          {!data && !w.error && <p className={styles.lead}>Loading…</p>}
          {data && (
            <>
              <div className={styles.card}>
                <h2>Add to the master list</h2>
                <div className={styles.inline}>
                  <label className={styles.field}>
                    Product
                    <select value={productId} disabled={!!w.busy} onChange={(e) => setProductId(e.target.value)}>
                      <option value="">Choose the product</option>
                      {live.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className={styles.field}>
                    Variety
                    <input value={name} maxLength={120} placeholder="e.g. Lapins" disabled={!!w.busy || !productId} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addVariety()} />
                  </label>
                  <button type="button" className={styles.primary} disabled={!!w.busy || !productId || !name.trim()} onClick={addVariety}>
                    {w.busy === 'variety' ? 'Adding…' : 'Add variety'}
                  </button>
                </div>
                <div className={styles.inline}>
                  <label className={styles.field}>
                    New product
                    <input value={productName} maxLength={60} placeholder="e.g. Plums" disabled={!!w.busy} onChange={(e) => setProductName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addProduct()} />
                  </label>
                  <button type="button" className={styles.secondary} disabled={!!w.busy || !productName.trim()} onClick={addProduct}>
                    {w.busy === 'product' ? 'Adding…' : 'Add product'}
                  </button>
                </div>
              </div>

              <div className={styles.tableCard}>
                <table className={styles.table} aria-label="Master list">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Variety</th>
                      <th>Clients</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((p) => {
                      const vs = p.varieties.filter((v) => showArchived || !v.archived_at)
                      const productRow = (
                        <tr key={p.id} data-testid="product-row">
                          <td>
                            <strong>{p.name}</strong>
                            {p.archived_at ? ' (archived)' : ''}
                          </td>
                          <td className={styles.hint}>{vs.length ? '' : 'No varieties yet'}</td>
                          <td />
                          <td>
                            <button type="button" className={styles.secondary} disabled={!!w.busy} aria-label={`${p.archived_at ? 'Restore' : 'Archive'} ${p.name}`} onClick={() => toggle('products', p.id, p.name, !!p.archived_at)}>
                              {p.archived_at ? 'Restore' : 'Archive'}
                            </button>
                          </td>
                        </tr>
                      )
                      return [
                        productRow,
                        ...vs.map((v) => (
                          <tr key={v.id} data-testid="master-row">
                            <td className={styles.hint}>{p.name}</td>
                            <td>
                              {v.name}
                              {v.archived_at ? ' (archived)' : ''}
                            </td>
                            <td>{v.clients}</td>
                            <td>
                              <button
                                type="button"
                                className={styles.secondary}
                                disabled={!!w.busy}
                                aria-label={`${v.archived_at ? 'Restore' : 'Archive'} ${p.name} / ${v.name}`}
                                onClick={() => toggle('master-varieties', v.id, `${p.name} / ${v.name}`, !!v.archived_at)}
                              >
                                {v.archived_at ? 'Restore' : 'Archive'}
                              </button>
                            </td>
                          </tr>
                        )),
                      ]
                    })}
                  </tbody>
                </table>
              </div>
              {archivedCount > 0 && (
                <label className={styles.hint}>
                  <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived ({archivedCount})
                </label>
              )}

              <div className={styles.tableCard}>
                <table className={styles.table} aria-label="Clients">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Varieties</th>
                      <th>Need a product</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.clients.map((c) => (
                      <tr key={c.id} className={styles.rowLink}>
                        <td>
                          <Link to={`/god-mode/clients/${c.id}/varieties`}>{c.name}</Link>
                        </td>
                        <td>{c.varieties}</td>
                        <td className={c.needs_product ? styles.breach : undefined}>{c.needs_product || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </PageBody>
    </>
  )
}

// One client's list: give it varieties from the master list or its own, set the product of one carried over without,
// archive and restore, and take its own variety into the master list so other clients can be given it.
export function ClientVarietiesPage() {
  const { id } = useParams()
  const { user } = useAuth()
  const [data, setData] = useState<{ client: { id: string; name: string }; products: VarietyProduct[]; varieties: SiteVariety[] } | null>(null)
  const [productId, setProductId] = useState('')
  const [name, setName] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const base = `/api/god-mode/clients/${id}/varieties`
  const load = async () => setData(await apiJson(user, base))
  const w = useWrites(load)

  useEffect(() => {
    load().catch((e) => w.setError(message(e, 'Could not load the varieties')))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id])

  const live = (data?.varieties || []).filter((v) => !v.archived_at)
  const archived = (data?.varieties || []).filter((v) => v.archived_at)
  const product = data?.products.find((p) => p.id === productId)
  const trimmed = name.trim().replace(/\s+/g, ' ')
  const master = product?.varieties.find((v) => v.name.toLowerCase() === trimmed.toLowerCase())
  const taken = new Set(live.map((v) => v.name.toLowerCase()))

  const add = async () => {
    if (!product || !trimmed) return
    if (await w.run('add', async () => {
      const body = master ? { master_variety_id: master.id } : { product_id: product.id, name: trimmed }
      const r = await apiJson<{ variety: SiteVariety }>(user, base, { method: 'POST', body })
      return `${r.variety.product} / ${r.variety.name} added${master ? ' from the master list' : ' as its own variety'}.`
    })) setName('')
  }
  const act = (v: SiteVariety, action: 'archive' | 'restore' | 'promote') =>
    w.run(v.id, async () => {
      await apiJson(user, `${base}/${v.id}/${action}`, { method: 'POST', body: {} })
      return action === 'promote' ? `${v.name} is on the master list under ${v.product} now.` : `${v.name} ${action}d.`
    })
  const setProduct = (v: SiteVariety, pid: string) =>
    pid &&
    w.run(v.id, async () => {
      const r = await apiJson<{ variety: SiteVariety }>(user, `${base}/${v.id}/product`, { method: 'PUT', body: { product_id: pid } })
      return `${v.name} is now under ${r.variety.product}.`
    })

  return (
    <>
      <PageHeader title={data ? `Varieties · ${data.client.name}` : 'Varieties'} />
      <PageBody>
        <div className={styles.stack}>
          <div>
            <Link to="/god-mode/varieties" className={styles.secondary}>
              ← All varieties
            </Link>{' '}
            <Link to={`/god-mode/clients/${id}`} className={styles.secondary}>
              Client
            </Link>
          </div>
          {w.error && <p className={styles.error} role="alert">{w.error}</p>}
          {w.notice && <p className={styles.saved} role="status">{w.notice}</p>}
          {!data && !w.error && <p className={styles.lead}>Loading…</p>}
          {data && (
            <>
              <div className={styles.card}>
                <h2>Give the client a variety</h2>
                <div className={styles.inline}>
                  <label className={styles.field}>
                    Product
                    <select value={productId} disabled={!!w.busy} onChange={(e) => setProductId(e.target.value)}>
                      <option value="">Choose the product</option>
                      {data.products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className={styles.field}>
                    Variety
                    <input value={name} maxLength={120} list="gm-master-list" disabled={!!w.busy || !product} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
                    <datalist id="gm-master-list">
                      {(product?.varieties || [])
                        .filter((v) => !taken.has(v.name.toLowerCase()))
                        .map((v) => (
                          <option key={v.id} value={v.name} />
                        ))}
                    </datalist>
                  </label>
                  <button type="button" className={styles.primary} disabled={!!w.busy || !product || !trimmed || taken.has(trimmed.toLowerCase())} onClick={add}>
                    {w.busy === 'add' ? 'Adding…' : 'Add variety'}
                  </button>
                </div>
                {product && trimmed && (
                  <span className={styles.hint}>
                    {taken.has(trimmed.toLowerCase()) ? `${trimmed} is already on the list.` : master ? 'From the master list.' : `The client's own variety of ${product.name}.`}
                  </span>
                )}
              </div>

              <div className={styles.tableCard}>
                <table className={styles.table} aria-label="Client varieties">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Variety</th>
                      <th>Source</th>
                      <th>Rows · jobs</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {live.length === 0 && (
                      <tr>
                        <td colSpan={5} className={styles.empty}>
                          No varieties yet.
                        </td>
                      </tr>
                    )}
                    {[...live, ...(showArchived ? archived : [])].map((v) => (
                      <tr key={v.id} data-testid="client-variety-row">
                        <td>
                          {v.product ||
                            (v.archived_at ? (
                              ''
                            ) : (
                              <select className={styles.breach} aria-label={`Product of ${v.name}`} value="" disabled={!!w.busy} onChange={(e) => setProduct(v, e.target.value)}>
                                <option value="">Needs a product</option>
                                {data.products.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name}
                                  </option>
                                ))}
                              </select>
                            ))}
                        </td>
                        <td>
                          {v.name}
                          {v.archived_at ? ' (archived)' : ''}
                        </td>
                        <td>{v.master_variety_id ? 'Master list' : 'Own'}</td>
                        <td>
                          {v.rows} · {v.jobs}
                        </td>
                        <td>
                          {!v.master_variety_id && v.product_id && !v.archived_at && (
                            <button type="button" className={styles.secondary} disabled={!!w.busy} aria-label={`Add ${v.name} to the master list`} onClick={() => act(v, 'promote')}>
                              To master list
                            </button>
                          )}{' '}
                          <button
                            type="button"
                            className={styles.secondary}
                            disabled={!!w.busy}
                            aria-label={`${v.archived_at ? 'Restore' : 'Archive'} ${v.name}`}
                            onClick={() => act(v, v.archived_at ? 'restore' : 'archive')}
                          >
                            {v.archived_at ? 'Restore' : 'Archive'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {archived.length > 0 && (
                <label className={styles.hint}>
                  <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived ({archived.length})
                </label>
              )}
            </>
          )}
        </div>
      </PageBody>
    </>
  )
}
